import { create } from 'zustand';

/**
 * Shell-level UI state. TanStack Query owns server state; this store is
 * limited to ephemeral UI bits and user-tuneable shell preferences
 * (theme, sidebar collapse, brand identity).
 */
type Theme = 'light' | 'dark';

interface Brand {
  /** Short label shown next to the workspace dropdown — e.g. "Demo Indonesia". */
  name: string;
  /** Sub-label under the workspace name — usually the legal entity. */
  tagline: string;
  /** One- or two-character text mark, shown when no logo is uploaded. */
  mark: string;
  /** Optional uploaded logo, stored as a data URL. Overrides the text mark. */
  logoDataUrl: string | null;
}

// DEMO (kelola-group): hardcoded to the prospect's identity. The workspace
// switcher label is brand-driven (not API-driven) for now.
const DEFAULT_BRAND: Brand = {
  name: 'Kelola Mina Laut',
  tagline: 'PT Kelola Mina Laut',
  mark: 'K',
  logoDataUrl: null,
};

interface Locale {
  /** BCP-47 tag — must be one of i18next's supportedLngs. */
  language: 'id-ID' | 'en-US';
  /** Display format token consumed by the format helpers. */
  dateFormat: 'dd/MM/yyyy' | 'yyyy-MM-dd' | 'd MMM yyyy';
  /** Group / decimal separators. id = 1.234,56 / en = 1,234.56. */
  numberFormat: 'id-ID' | 'en-US';
  /** IANA tz string. */
  timezone: string;
  /** 0 = Sunday, 1 = Monday — drives date pickers. */
  firstDayOfWeek: 0 | 1;
}

const DEFAULT_LOCALE: Locale = {
  language: 'id-ID',
  dateFormat: 'dd/MM/yyyy',
  numberFormat: 'id-ID',
  timezone: 'Asia/Jakarta',
  firstDayOfWeek: 1,
};

interface UIState {
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
  togglePalette: () => void;

  // Copilot panel state. openCopilotWith() is invoked from places like the
  // nudge bar — it opens the panel AND seeds the input/auto-sends a prompt.
  // The panel component subscribes to (copilotOpen, copilotMinimized,
  // copilotSeedPrompt) and handles the actual UI.
  //
  // Floating chat lifecycle: the panel is always mounted in the shell so the
  // conversation survives close/reopen cycles. Three visual states:
  //   - hidden      (!open && !minimized): nothing on screen, sparkle in
  //                 TopChrome reopens.
  //   - minimized   (!open && minimized):  a small chip in the bottom-right
  //                 corner; click to restore.
  //   - open        (open):                the floating card.
  copilotOpen: boolean;
  copilotMinimized: boolean;
  copilotSeedPrompt: string | null;
  /** Set by external callers (⌘K palette) to resume a specific session on open. */
  copilotResumeSessionId: string | null;
  openCopilotWith: (prompt: string) => void;
  /** Open the panel and queue a session id to load. */
  openCopilotSession: (sessionId: string) => void;
  /** Restore from minimized OR open from hidden. */
  openCopilot: () => void;
  /** Collapse to chip; preserves conversation state. */
  minimizeCopilot: () => void;
  /** Hide entirely; preserves conversation state. */
  closeCopilot: () => void;
  /** Called by the panel after it consumes the seed so we don't re-send. */
  clearCopilotSeed: () => void;
  /** Called by the panel after it consumes a resume id. */
  clearCopilotResume: () => void;

  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  /** Off-canvas nav drawer for phones / narrow tablets. Distinct from
   *  sidebarCollapsed — collapse shrinks the desktop rail to 64px,
   *  mobileNavOpen reveals the same nav as an overlay drawer. */
  mobileNavOpen: boolean;
  openMobileNav: () => void;
  closeMobileNav: () => void;
  toggleMobileNav: () => void;

  /** Settings nav is a single-open accordion: the key of the one open group,
   *  `null` = all collapsed, `undefined` = not chosen yet (the layout then
   *  defaults to the active section's group). Persisted. */
  settingsExpandedGroup: string | null | undefined;
  setSettingsExpandedGroup: (groupKey: string | null) => void;

  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;

  brand: Brand;
  setBrand: (patch: Partial<Brand>) => void;
  resetBrand: () => void;

  locale: Locale;
  setLocale: (patch: Partial<Locale>) => void;
  resetLocale: () => void;
}

const STORAGE_KEY_THEME  = 'logica.theme';
// DEMO (kelola-group): keys bumped so any stale "Demo Indonesia" brand / en-US
// locale left in a browser is ignored and the Kelola defaults take effect.
const STORAGE_KEY_BRAND  = 'logica.brand.kml';
const STORAGE_KEY_LOCALE = 'logica.locale.id';
const STORAGE_KEY_SETTINGS_GROUP = 'logica.settingsNavGroup';

function initialTheme(): Theme {
  // DEMO (kelola-group): light mode only — ignore stored/system preference.
  return 'light';
}

function applyTheme(t: Theme) {
  const root = document.documentElement;
  if (t === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
  localStorage.setItem(STORAGE_KEY_THEME, t);
}

function loadBrand(): Brand {
  if (typeof window === 'undefined') return DEFAULT_BRAND;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_BRAND);
    if (!raw) return DEFAULT_BRAND;
    const parsed = JSON.parse(raw) as Partial<Brand>;
    return { ...DEFAULT_BRAND, ...parsed };
  } catch {
    return DEFAULT_BRAND;
  }
}

function saveBrand(b: Brand) {
  try { localStorage.setItem(STORAGE_KEY_BRAND, JSON.stringify(b)); } catch { /* quota; ignore */ }
}

function loadLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LOCALE);
    if (!raw) return DEFAULT_LOCALE;
    return { ...DEFAULT_LOCALE, ...(JSON.parse(raw) as Partial<Locale>) };
  } catch {
    return DEFAULT_LOCALE;
  }
}

function saveLocale(l: Locale) {
  try { localStorage.setItem(STORAGE_KEY_LOCALE, JSON.stringify(l)); } catch { /* ignore */ }
}

function loadSettingsExpandedGroup(): string | null | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SETTINGS_GROUP);
    if (raw === null) return undefined; // never chosen → defer to active group
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'string' || parsed === null ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function saveSettingsExpandedGroup(g: string | null) {
  try { localStorage.setItem(STORAGE_KEY_SETTINGS_GROUP, JSON.stringify(g)); } catch { /* ignore */ }
}

export const useUI = create<UIState>()((set, get) => ({
  paletteOpen: false,
  setPaletteOpen: (open) => set({ paletteOpen: open }),
  togglePalette: () => set({ paletteOpen: !get().paletteOpen }),

  copilotOpen: false,
  copilotMinimized: false,
  copilotSeedPrompt: null,
  copilotResumeSessionId: null,
  openCopilotWith: (prompt) => set({ copilotOpen: true, copilotMinimized: false, copilotSeedPrompt: prompt }),
  openCopilotSession: (sessionId) => set({ copilotOpen: true, copilotMinimized: false, copilotResumeSessionId: sessionId }),
  openCopilot: () => set({ copilotOpen: true, copilotMinimized: false }),
  minimizeCopilot: () => set({ copilotOpen: false, copilotMinimized: true }),
  closeCopilot: () => set({ copilotOpen: false, copilotMinimized: false }),
  clearCopilotSeed: () => set({ copilotSeedPrompt: null }),
  clearCopilotResume: () => set({ copilotResumeSessionId: null }),

  sidebarCollapsed: false,
  toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),

  mobileNavOpen: false,
  openMobileNav: () => set({ mobileNavOpen: true }),
  closeMobileNav: () => set({ mobileNavOpen: false }),
  toggleMobileNav: () => set({ mobileNavOpen: !get().mobileNavOpen }),

  settingsExpandedGroup: loadSettingsExpandedGroup(),
  setSettingsExpandedGroup: (groupKey) => {
    saveSettingsExpandedGroup(groupKey);
    set({ settingsExpandedGroup: groupKey });
  },

  theme: 'light',
  // DEMO (kelola-group): theme is locked to light — set/toggle are no-ops.
  setTheme: () => { applyTheme('light'); set({ theme: 'light' }); },
  toggleTheme: () => { applyTheme('light'); set({ theme: 'light' }); },

  brand: loadBrand(),
  setBrand: (patch) => {
    const next = { ...get().brand, ...patch };
    saveBrand(next);
    set({ brand: next });
  },
  resetBrand: () => {
    saveBrand(DEFAULT_BRAND);
    set({ brand: DEFAULT_BRAND });
  },

  locale: loadLocale(),
  setLocale: (patch) => {
    const next = { ...get().locale, ...patch };
    saveLocale(next);
    set({ locale: next });
  },
  resetLocale: () => {
    saveLocale(DEFAULT_LOCALE);
    set({ locale: DEFAULT_LOCALE });
  },
}));

if (typeof window !== 'undefined') {
  applyTheme(initialTheme());
}
